import { ref, computed } from 'vue';
import type { User, UserUpdate } from '@/types/auth';
import { usersApi } from '@/lib/api/users';
import { useApiError } from '@/features/auth/composables/useApiError';
import { isAxiosError } from 'axios';

export interface UserOperationsOptions {
  onUserUpdate?: (user: User) => void;
  onUserDelete?: () => void;
}

/**
 * Reusable user operations composable
 * Provides common user management patterns that can be used by application stores
 */
export function useUserOperations(options: UserOperationsOptions = {}) {
  const { onUserUpdate, onUserDelete } = options;
  const { handleError } = useApiError();

  // State
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const updateSuccess = ref(false);

  // Computed getters
  const hasError = computed(() => !!error.value);
  const isUpdating = computed(() => isLoading.value);

  // Actions
  const updateUserProfile = async (userData: Partial<UserUpdate>): Promise<User> => {
    try {
      isLoading.value = true;
      error.value = null;
      updateSuccess.value = false;

      const updatedUser = await usersApi.updateCurrentUser(userData);
      updateSuccess.value = true;
      onUserUpdate?.(updatedUser);

      return updatedUser;
    } catch (err: unknown) {
      error.value = 'Failed to update profile';
      if (isAxiosError(err)) {
        handleError(err);
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<User> => {
    try {
      isLoading.value = true;
      error.value = null;
      updateSuccess.value = false;

      const updatedUser = await usersApi.changePassword(currentPassword, newPassword);
      updateSuccess.value = true;
      onUserUpdate?.(updatedUser);

      return updatedUser;
    } catch (err: unknown) {
      error.value = 'Failed to change password';
      if (isAxiosError(err)) {
        handleError(err);
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateEmail = async (newEmail: string): Promise<User> => {
    try {
      isLoading.value = true;
      error.value = null;
      updateSuccess.value = false;

      const updatedUser = await usersApi.updateCurrentUser({
        email: newEmail,
      });

      updateSuccess.value = true;
      onUserUpdate?.(updatedUser);

      return updatedUser;
    } catch (err: unknown) {
      error.value = 'Failed to update email';
      if (isAxiosError(err)) {
        handleError(err);
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteCurrentUser = async (password: string): Promise<void> => {
    try {
      isLoading.value = true;
      error.value = null;
      updateSuccess.value = false;

      await usersApi.deleteCurrentUser(password);
      updateSuccess.value = true;
      onUserDelete?.();
    } catch (err: unknown) {
      error.value = 'Failed to delete account';
      if (isAxiosError(err)) {
        handleError(err);
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  const clearUpdateSuccess = () => {
    updateSuccess.value = false;
  };

  const resetState = () => {
    isLoading.value = false;
    error.value = null;
    updateSuccess.value = false;
  };

  return {
    // State
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    updateSuccess: computed(() => updateSuccess.value),

    // Computed getters
    hasError,
    isUpdating,

    // Actions
    updateUserProfile,
    changePassword,
    updateEmail,
    deleteCurrentUser,
    clearError,
    clearUpdateSuccess,
    resetState,
  };
}
